import { decimalFormat, translate } from '../../utils';

export const INITIAL_VISIBLE_HOTEL_LIMIT = 10;

export const PERIOD_OPTIONS = [
    {
        value: 'daily',
        yesterdayComparisonPeriodTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_YESTERDAY',
        tomorrowComparisonPeriodTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_TOMORROW',
        previousComparisonPeriodTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_PREVIOUS_DAY',
        nextComparisonPeriodTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_NEXT_DAY',
        revenueLabelTranslationKey: 'PORTFOLIO_DASHBOARD_REVENUE_TODAY',
        extendedRevenueLabelTranslationKey: 'PORTFOLIO_DASHBOARD_REVENUE_THIS_WEEK',
        extendedPreviousComparisonTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_PREVIOUS_WEEK',
        extendedNextComparisonTranslationKey: 'PORTFOLIO_DASHBOARD_COMPARED_TO_NEXT_WEEK',
    },
];

const DEFAULT_PERIOD = PERIOD_OPTIONS[0].value;
const DEFAULT_OVERVIEW_METRIC = 'occupancy';

/**
 * @param {import('./portfolioDashboard.types').PortfolioPeriod} selectedPeriod
 * @returns {import('./portfolioDashboard.types').PortfolioMetricDefinition[]}
 */
export function getPortfolioMetricDefinitions(selectedPeriod) {
    const periodOption = PERIOD_OPTIONS.find((periodOption) => periodOption.value === selectedPeriod);

    return [
        {
            metricKey: 'occupiedRooms',
            labelTranslationKey: 'OCCP_ROOM',
            valueFormat: 'integer',
            previousChangePercentageField: 'previousOccupiedRoomsChangePercentage',
            nextChangePercentageField: 'nextOccupiedRoomsChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'occupancy',
            labelTranslationKey: 'Occupancy',
            valueFormat: 'percent',
            previousChangePercentageField: 'previousOccupancyChangePercentage',
            nextChangePercentageField: 'nextOccupancyChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'adr',
            labelTranslationKey: 'ADR',
            valueFormat: 'currency',
            previousChangePercentageField: 'previousAdrChangePercentage',
            nextChangePercentageField: 'nextAdrChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'revPAR',
            labelTranslationKey: 'REV_PAR',
            valueFormat: 'currency',
            previousChangePercentageField: 'previousRevPARChangePercentage',
            nextChangePercentageField: 'nextRevPARChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'revenue',
            labelTranslationKey: periodOption.revenueLabelTranslationKey,
            valueFormat: 'compactCurrency',
            previousChangePercentageField: 'previousRevenueChangePercentage',
            nextChangePercentageField: 'nextRevenueChangePercentage',
            usesExtendedComparison: false,
        },
        {
            metricKey: 'extendedRevenue',
            labelTranslationKey: periodOption.extendedRevenueLabelTranslationKey,
            valueFormat: 'compactCurrency',
            previousChangePercentageField: 'previousRevenueChangePercentage',
            nextChangePercentageField: 'nextRevenueChangePercentage',
            usesExtendedComparison: true,
        },
    ];
}

/**
 * @param {string[] | null | undefined} selectedPropertiesKeys
 * @param {'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'} selectedPeriod
 * @param {string} selectedDate
 * @param {string} today
 * @param {import('./portfolioDashboard.types').PortfolioOverviewMetricKey} selectedOverviewMetric
 * @returns {URLSearchParams}
 */
export function buildSearchParams(selectedPropertiesKeys, selectedPeriod, selectedDate, today, selectedOverviewMetric) {
    const searchParams = new URLSearchParams();

    if (selectedPeriod !== DEFAULT_PERIOD) {
        searchParams.set('selectedPeriod', selectedPeriod);
    }

    if (selectedDate !== today) {
        searchParams.set('selectedDate', selectedDate);
    }

    if (selectedOverviewMetric !== DEFAULT_OVERVIEW_METRIC) {
        searchParams.set('overviewMetric', selectedOverviewMetric);
    }

    selectedPropertiesKeys?.forEach((propertyKey) => searchParams.append('selectedPropertiesKeys[]', propertyKey));

    return searchParams;
}

const PERCENTAGE_DIFF_APPEARANCE = {
    1: {
        colorClass: 'text-green',
        iconClass: 'fa fa-angle-up',
        signPrefix: '+',
    },
    [-1]: {
        colorClass: 'text-red',
        iconClass: 'fa fa-angle-down',
        signPrefix: '',
    },
    0: {
        colorClass: 'text-muted',
        iconClass: '',
        signPrefix: '',
    },
};

/**
 * @param {number} changePercent
 * @param {boolean} [higherIsBetter=true]
 * @returns {{ colorClass: string, iconClass: string, signPrefix: string }}
 */
export function getPercentageDiffAppearance(changePercent, higherIsBetter = true) {
    const changeSign = Math.sign(changePercent);
    const { iconClass, signPrefix } = PERCENTAGE_DIFF_APPEARANCE[changeSign];
    const { colorClass } = PERCENTAGE_DIFF_APPEARANCE[higherIsBetter ? changeSign : -changeSign];

    return { colorClass, iconClass, signPrefix };
}

const COMPACT_CURRENCY_MILLIONS_THRESHOLD = 500000;

/**
 * @param {'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'} selectedPeriod
 * @param {boolean} isTodaySelected
 * @returns {string}
 */
export function getMetricPreviousComparisonTranslationKey(selectedPeriod, isTodaySelected, useExtendedComparison = false) {
    const periodOption = PERIOD_OPTIONS.find((periodOption) => periodOption.value === selectedPeriod);

    if (useExtendedComparison) return periodOption.extendedPreviousComparisonTranslationKey;
    if (isTodaySelected) return periodOption.yesterdayComparisonPeriodTranslationKey;

    return periodOption.previousComparisonPeriodTranslationKey;
}

/**
 * @param {'daily' | 'weekly' | 'monthly' | 'quarterly' | 'annual'} selectedPeriod
 * @param {boolean} isTodaySelected
 * @returns {string}
 */
export function getMetricNextComparisonTranslationKey(selectedPeriod, isTodaySelected, useExtendedComparison = false) {
    const periodOption = PERIOD_OPTIONS.find((periodOption) => periodOption.value === selectedPeriod);

    if (useExtendedComparison) return periodOption.extendedNextComparisonTranslationKey;
    if (isTodaySelected) return periodOption.tomorrowComparisonPeriodTranslationKey;

    return periodOption.nextComparisonPeriodTranslationKey;
}

/**
 * @param {{ value: number, valueFormat: 'integer' | 'percent' | 'currency' | 'compactCurrency' | string }} metric
 * @param {string} currency
 * @returns {string}
 */
export function formatMetricValue(metric, currency) {
    switch (metric.valueFormat) {
        case 'integer':
            return decimalFormat(metric.value, 0);
        case 'percent':
            return `${decimalFormat(metric.value * 100, 1)} %`;
        case 'currency':
            return `${decimalFormat(metric.value, 2)} ${currency}`;
        case 'compactCurrency':
            return formatCompactCurrency(metric.value, currency);
        default:
            return String(metric.value);
    }
}

/**
 * @param {number} value
 * @param {string} currency
 * @returns {string}
 */
function formatCompactCurrency(value, currency) {
    if (value >= COMPACT_CURRENCY_MILLIONS_THRESHOLD) {
        return translate('frontend', 'PORTFOLIO_DASHBOARD_COMPACT_CURRENCY_MILLIONS', {
            value: decimalFormat(value / 1000000, 2),
            currency,
        });
    }

    return translate('frontend', 'PORTFOLIO_DASHBOARD_COMPACT_CURRENCY_THOUSANDS', {
        value: decimalFormat(value / 1000, 2),
        currency,
    });
}
